import React, { useLayoutEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    DragStartEvent,
    DragEndEvent
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { List, GripVertical } from 'lucide-react';
import clsx from 'clsx';
import { Tooltip } from '@common/index';
import { Group } from '@/contexts/AdminContext/dashboardProvider';

interface StructureMenuProps {
    groups: Group[];
    onReorder: (newGroups: Group[]) => void;
    onScrollTo: (groupId: string) => void;
}

// Pure Presentational Component
const StructureItem = ({
    title,
    widgetCount,
    onClick,
    dragHandleProps,
    style,
    forwardedRef,
    isDragging,
    isOverlay
}: {
    title: string;
    widgetCount: number;
    onClick: () => void;
    dragHandleProps: any;
    style: React.CSSProperties;
    forwardedRef: (node: HTMLElement | null) => void;
    isDragging: boolean;
    isOverlay: boolean;
}) => {
    return (
        <div
            ref={forwardedRef}
            style={style}
            className={clsx(
                "flex items-center gap-3 p-3 rounded-lg mb-2 group select-none touch-none will-change-transform",
                !(isDragging || isOverlay) && "transition-shadow duration-200",
                // Default style (not dragging, not overlay)
                !isDragging && !isOverlay && "bg-white border border-gray-100 shadow-sm hover:shadow-md",
                // Placeholder style (original item while dragging)
                isDragging && "bg-white border-2 border-dashed border-primary opacity-60 shadow-none",
                // Overlay style (flying item)
                isOverlay && "bg-white border-2 border-primary shadow-lg cursor-grabbing z-50"
            )}
        >
            <div
                {...dragHandleProps}
                className={clsx(
                    "text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100",
                    isOverlay ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing"
                )}
            >
                <GripVertical size={16} />
            </div>

            <div
                onClick={onClick}
                className="flex-1 min-w-0 cursor-pointer hover:text-primary transition-colors"
            >
                <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-primary">{title || 'Untitled Group'}</h4>
                <span className="text-xs text-gray-500">{widgetCount} Charts</span>
            </div>
        </div>
    );
};

// Sortable Item Component Wrapper
const SortableItem = ({ id, title, widgetCount, onClick }: {
    id: string;
    title: string;
    widgetCount: number;
    onClick: () => void;
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition: isDragging ? undefined : transition,
        willChange: 'transform',
    };

    return (
        <StructureItem
            forwardedRef={setNodeRef}
            style={style}
            title={title}
            widgetCount={widgetCount}
            onClick={onClick}
            dragHandleProps={{ ...attributes, ...listeners }}
            isDragging={isDragging}
            isOverlay={false}
        />
    );
};

const StructureMenu: React.FC<StructureMenuProps> = ({ groups, onReorder, onScrollTo }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [activeId, setActiveId] = useState<string | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    useLayoutEffect(() => {
        if (!isOpen || !buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const menuWidth = 320;
        let left = rect.left + (rect.width / 2) - (menuWidth / 2);

        if (left < 16) left = 16;
        if (left + menuWidth > window.innerWidth - 16) {
            left = window.innerWidth - menuWidth - 16;
        }

        setMenuPosition({
            top: rect.bottom + 12,
            left,
        });
    }, [isOpen]);

    const toggleMenu = () => {
        setIsOpen((prev) => !prev);
    };

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over) {
            setActiveId(null);
            return;
        }

        if (active.id !== over.id) {
            const oldIndex = groups.findIndex((g) => g.id === active.id);
            const newIndex = groups.findIndex((g) => g.id === over.id);

            onReorder(arrayMove(groups, oldIndex, newIndex));
        }
        setActiveId(null);
    };

    const handleDragCancel = () => {
        setActiveId(null);
    };

    const activeGroup = activeId ? groups.find(g => g.id === activeId) : null;

    return (
        <>
            {/* Toggle Button */}
            <Tooltip content="Dashboard Structure" side="bottom">
                <button
                    ref={buttonRef}
                    onClick={toggleMenu}
                    className={clsx(
                        "p-2 bg-white border border-gray-200 rounded-xl transition-all shadow-sm",
                        isOpen ? "bg-gray-100 text-[#1A162F] ring-2 ring-primary/20" : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    )}
                >
                    <List className="w-4 h-4" />
                </button>
            </Tooltip>

            {/* Menu Content */}
            {isOpen && createPortal(
                <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-[140]" onClick={() => setIsOpen(false)} />

                    {/* Menu Panel */}
                    <div
                        className={clsx(
                            "fixed z-[9999] w-80 rounded-2xl shadow-2xl border border-gray-100 p-4",
                            activeId ? "bg-white" : "bg-white/95 backdrop-blur-md"
                        )}
                        style={{
                            top: menuPosition.top,
                            left: menuPosition.left
                        }}
                    >
                        <div className="flex items-center justify-between mb-4 px-2">
                            <h3 className="font-bold text-gray-900">Dashboard Structure</h3>
                            <span className="text-xs font-medium px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                                {groups.length} Groups
                            </span>
                        </div>

                        <div className="max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                onDragCancel={handleDragCancel}
                            >
                                <SortableContext
                                    items={groups.map(g => g.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {groups.map((group) => (
                                        <SortableItem
                                            key={group.id}
                                            id={group.id}
                                            title={group.title}
                                            widgetCount={group.items_id?.length || 0}
                                            onClick={() => {
                                                onScrollTo(group.id);
                                                setIsOpen(false);
                                            }}
                                        />
                                    ))}
                                </SortableContext>

                                <DragOverlay dropAnimation={null} adjustScale={false}>
                                    {activeGroup ? (
                                        <StructureItem
                                            title={activeGroup.title}
                                            widgetCount={activeGroup.items_id?.length || 0}
                                            onClick={() => {}}
                                            dragHandleProps={{}}
                                            forwardedRef={() => {}}
                                            isDragging={false}
                                            isOverlay
                                            style={{ willChange: 'transform' }}
                                        />
                                    ) : null}
                                </DragOverlay>
                            </DndContext>

                            {groups.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-8 text-gray-400 border-2 border-dashed border-gray-100 rounded-lg">
                                    <p className="text-sm">No groups yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </>
    );
};

export default StructureMenu;
